import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Account, DashboardSummary, ChartData, TopModelData } from '../types/api';
import { StorageManager } from '../utils/storageManager';

// Helper to get default dates
const getInitialEndDate = () => new Date();
const getInitialStartDate = (endDate: Date) => {
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 29); // Default to 30 days
  return startDate;
};

// Загружаем начальное состояние из localStorage
const initialPreferences = StorageManager.getPreferences();
const initialEndDate = getInitialEndDate();
const initialStartDate = getInitialStartDate(initialEndDate);

// Function to format date to YYYY-MM-DD
const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
}

export interface AnalyticsState {
  accounts: Account[];
  selectedAccountId: number | string | null;
  startDate: string;
  endDate: string;
  isComparisonMode: boolean;
  previousStartDate: string | null;
  previousEndDate: string | null;
  summary: DashboardSummary | null;
  spendsChartData: ChartData | null;
  ctrChartData: ChartData | null;
  topModels: TopModelData[];
  spendsByAccounts: Array<{account: string, spends: number}>;
  loading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  accounts: [],
  selectedAccountId: initialPreferences.selectedAccountId,
  startDate: formatDate(initialStartDate),
  endDate: formatDate(initialEndDate),
  isComparisonMode: false,
  previousStartDate: null,
  previousEndDate: null,
  summary: null,
  spendsChartData: null,
  ctrChartData: null,
  topModels: [],
  spendsByAccounts: [],
  loading: false,
  error: null,
};

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setAccounts: (state, action: PayloadAction<Account[]>) => {
      state.accounts = action.payload;
    },
    setSelectedAccountId: (state, action: PayloadAction<number | string | null>) => {
      state.selectedAccountId = action.payload;
      StorageManager.updatePreference('selectedAccountId', action.payload);
    },
    setDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
        state.startDate = action.payload.startDate;
        state.endDate = action.payload.endDate;
        state.isComparisonMode = false;
        state.previousStartDate = null;
        state.previousEndDate = null;
    },
    setComparisonDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string; compareStartDate: string; compareEndDate: string }>) => {
        state.startDate = action.payload.startDate;
        state.endDate = action.payload.endDate;
        state.isComparisonMode = true;
        state.previousStartDate = action.payload.compareStartDate;
        state.previousEndDate = action.payload.compareEndDate;
    },
    setSummary: (state, action: PayloadAction<DashboardSummary | null>) => {
      state.summary = action.payload;
    },
    setSpendsChartData: (state, action: PayloadAction<ChartData | null>) => {
      state.spendsChartData = action.payload;
    },
    setCtrChartData: (state, action: PayloadAction<ChartData | null>) => {
      state.ctrChartData = action.payload;
    },
    setTopModels: (state, action: PayloadAction<TopModelData[]>) => {
      state.topModels = action.payload;
    },
    setSpendsByAccounts: (state, action: PayloadAction<Array<{account: string, spends: number}>>) => {
      state.spendsByAccounts = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetToInitialState: (state) => {
      const endDate = getInitialEndDate();
      const startDate = getInitialStartDate(endDate);
      state.selectedAccountId = initialPreferences.selectedAccountId;
      state.startDate = formatDate(startDate);
      state.endDate = formatDate(endDate);
      state.summary = null;
      state.spendsChartData = null;
      state.ctrChartData = null;
      state.topModels = [];
      state.spendsByAccounts = [];
      state.loading = false;
      state.error = null;
      state.isComparisonMode = false;
      state.previousStartDate = null;
      state.previousEndDate = null;
    },
  },
});

export const {
  setAccounts,
  setSelectedAccountId,
  setDateRange,
  setComparisonDateRange,
  setSummary,
  setSpendsChartData,
  setCtrChartData,
  setTopModels,
  setSpendsByAccounts,
  setLoading,
  setError,
  clearError,
  resetToInitialState,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;